import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, Card, CardContent, CircularProgress, CssBaseline, Divider, IconButton, Stack, ThemeProvider, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { messageFromError } from '../components/ApiError';
import { theme } from '../theme';
import type { ApiResponse } from '../types';

type Checkout = {
  business: { name: string; supportEmail?: string | null; logoUrl?: string | null; primaryColor: string };
  invoice: {
    number: string;
    description?: string | null;
    currency: string;
    subtotal: string;
    discountAmount: string;
    taxAmount: string;
    processingFee: string;
    grandTotal: string;
    dueDate?: string | null;
    items: Array<{ description: string; quantity: string; unitAmount: string; totalAmount: string }>;
  };
};

const value = (amount: string, currency: string) => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amount));

function ResultCard({ successful, onRetry }: { successful: boolean; onRetry: () => void }) {
  return <Card>
    <CardContent sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
      <Stack spacing={2.5} alignItems="center">
        <Box
          display="grid"
          sx={{ placeItems: 'center' }}
          width={88}
          height={88}
          borderRadius="50%"
          bgcolor={successful ? 'success.light' : 'error.light'}
          color={successful ? 'success.dark' : 'error.dark'}
        >
          {successful ? <CheckCircleRoundedIcon sx={{ fontSize: 52 }} /> : <CancelRoundedIcon sx={{ fontSize: 52 }} />}
        </Box>
        <Box>
          <Typography variant="h4" gutterBottom>{successful ? 'Payment successful' : 'Payment not completed'}</Typography>
          <Typography color="text.secondary" maxWidth={440}>
            {successful
              ? 'Thank you. Your payment has been completed successfully. A receipt will be sent to the email used at checkout.'
              : 'Your payment was cancelled or could not be completed. You have not been charged.'}
          </Typography>
        </Box>
        {successful
          ? <Typography variant="body2" color="text.secondary">You can safely close this page.</Typography>
          : <Button variant="contained" size="large" onClick={onRetry} sx={{ px: 4, py: 1.25 }}>Try payment again</Button>}
      </Stack>
    </CardContent>
  </Card>;
}

export function CheckoutPage() {
  const { token = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const uiTheme = useMemo(() => theme(mode), [mode]);
  const returnStatus = params.get('status');
  const successful = returnStatus === 'success';
  const unsuccessful = returnStatus === 'cancelled' || returnStatus === 'failed' || returnStatus === 'failure';
  const isResult = successful || unsuccessful;

  const checkout = useQuery({
    queryKey: ['checkout', token],
    queryFn: async () => (await api.get<ApiResponse<Checkout>>(`/public/payment-links/${token}`)).data.data,
    enabled: !isResult,
    retry: false
  });
  const pay = useMutation({
    mutationFn: async () => (await api.post<ApiResponse<{ checkoutUrl: string }>>(`/public/payment-links/${token}/checkout`, {})).data.data,
    onSuccess: (data) => { window.location.assign(data.checkoutUrl); }
  });

  return <ThemeProvider theme={uiTheme}>
    <CssBaseline />
    <Box minHeight="100vh" bgcolor="background.default" py={{ xs: 2, sm: 6 }} px={2}>
      <Box maxWidth={640} mx="auto">
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h5" fontWeight={800} color="primary">{checkout.data?.business.name ?? 'Payment'}</Typography>
          <IconButton onClick={() => setMode((current) => current === 'light' ? 'dark' : 'light')} aria-label="Toggle color mode">
            {mode === 'light' ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}
          </IconButton>
        </Box>

        {isResult && <ResultCard successful={successful} onRetry={() => setParams({})} />}
        {!isResult && checkout.isLoading && <Box textAlign="center" py={10}><CircularProgress /></Box>}
        {!isResult && checkout.isError && <Alert severity="error">{messageFromError(checkout.error)}</Alert>}
        {!isResult && checkout.data && <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="overline" color="text.secondary">Invoice {checkout.data.invoice.number}</Typography>
                <Typography variant="h4">{value(checkout.data.invoice.grandTotal, checkout.data.invoice.currency)}</Typography>
                {checkout.data.invoice.description && <Typography color="text.secondary" mt={1}>{checkout.data.invoice.description}</Typography>}
              </Box>
              <Divider />
              <Stack spacing={1.25}>
                {checkout.data.invoice.items.map((item, index) => <Box key={`${item.description}-${index}`} display="flex" justifyContent="space-between" gap={2}>
                  <Typography>{item.description} <Typography component="span" color="text.secondary" variant="body2">× {item.quantity}</Typography></Typography>
                  <Typography>{value(item.totalAmount, checkout.data.invoice.currency)}</Typography>
                </Box>)}
                <Box display="flex" justifyContent="space-between"><Typography color="text.secondary">Subtotal</Typography><Typography>{value(checkout.data.invoice.subtotal, checkout.data.invoice.currency)}</Typography></Box>
                {Number(checkout.data.invoice.discountAmount) > 0 && <Box display="flex" justifyContent="space-between"><Typography color="text.secondary">Discount</Typography><Typography>−{value(checkout.data.invoice.discountAmount, checkout.data.invoice.currency)}</Typography></Box>}
                {Number(checkout.data.invoice.taxAmount) > 0 && <Box display="flex" justifyContent="space-between"><Typography color="text.secondary">Tax</Typography><Typography>{value(checkout.data.invoice.taxAmount, checkout.data.invoice.currency)}</Typography></Box>}
                <Box display="flex" justifyContent="space-between"><Typography color="text.secondary">Processing fee</Typography><Typography>{value(checkout.data.invoice.processingFee, checkout.data.invoice.currency)}</Typography></Box>
              </Stack>
              <Divider />
              <Box display="flex" justifyContent="space-between" alignItems="baseline"><Typography variant="h6">Total due</Typography><Typography variant="h5" fontWeight={700}>{value(checkout.data.invoice.grandTotal, checkout.data.invoice.currency)}</Typography></Box>
              <Button variant="contained" size="large" onClick={() => pay.mutate()} disabled={pay.isPending} sx={{ py: 1.5, bgcolor: checkout.data.business.primaryColor || 'primary.main' }}>{pay.isPending ? 'Connecting to secure checkout…' : 'Pay securely'}</Button>
              {pay.isError && <Alert severity="error">{messageFromError(pay.error)}</Alert>}
              <Box display="flex" justifyContent="center" alignItems="center" gap={1}><LockRoundedIcon fontSize="small" color="action" /><Typography variant="caption" color="text.secondary">Payments are processed securely by Stripe.</Typography></Box>
              {checkout.data.business.supportEmail && <Typography align="center" variant="caption" color="text.secondary">Need help? {checkout.data.business.supportEmail}</Typography>}
            </Stack>
          </CardContent>
        </Card>}
      </Box>
    </Box>
  </ThemeProvider>;
}
